import { Reducer } from '@reduxjs/toolkit';
import { fork } from 'redux-saga/effects';
import { Saga } from 'redux-saga';

import { AppType } from '../enums';
import { appReducer, AppState } from './slices/appSlice';

import { vacancySaga } from '../../employer/units/vacancy/redux/sagas/vacancySaga';
import { vacancyReducer, VacancyState } from '../../employer/units/vacancy/redux/slices/vacancySlice';

// Worker
import { workerAppReducer, WorkerAppState } from '../../worker/redux/slices/workerAppSlice';
import { responsesSaga } from '../../employer/units/responses/redux/sagas/responsesSaga';
import { responsesReducer, ResponsesState } from '../../employer/units/responses/redux/slices/responsesSlice';
import {
    signUpReducer as workerAppSignUpReducer,
    SignUpState as WorkerAppSignUpState,
} from '../../worker/units/authorization/redux/slices/signUpSlice';
import {
    loginReducer as workerAppLoginReducer,
    LoginState as WorkerAppLoginState,
} from '../../worker/units/authorization/redux/slices/loginSlice';
import {
    passwordRecoveryReducer as workerAppPasswordRecoveryReducer,
    PasswordRecoveryState as WorkerAppPasswordRecoveryState,
} from '../../worker/units/authorization/redux/slices/passwordRecoverySlice';
import {
    settingsReducer as workerAppSettingsReducer,
    SettingsState as WorkerAppSettingsState,
} from '../../worker/units/settings/redux/slice/settingsSlice';
import {
    quickApplyReducer as workerAppQuickApplyReducer,
    QuickApplyState as WorkerAppQuickApplyState,
} from '../../worker/units/quick-apply/redux/slices/quickApplySlice';
import {
    quickSignUpReducer as workerAppQuickSignUpReducer,
    QuickSignUpState as WorkerAppQuickSignUpState,
} from '../../worker/units/authorization/redux/slices/quickSignUpSlice';

import { workerAppSaga } from '../../worker/redux/sagas/workerAppSaga';
import { authSaga as workerAppAuthSaga } from '../../worker/units/authorization/redux/sagas/authSaga';
import { settingsSaga as workerAppSettingsSaga } from '../../worker/units/settings/redux/sagas/settingsSaga';
import { quickApplySaga as workerAppQuickApplySaga } from '../../worker/units/quick-apply/redux/sagas/quickApplySaga';

// Recruiter
import { recruiterAppReducer, RecruiterAppState } from '../../recruiter/redux/slices/recruiterAppSlice';
import {
    signUpReducer as recruiterAppSignUpReducer,
    SignUpState as RecruiterAppSignUpState,
} from '../../recruiter/units/authorization/redux/slices/signUpSlice';
import {
    loginReducer as recruiterAppLoginReducer,
    LoginState as RecruiterAppLoginState,
} from '../../recruiter/units/authorization/redux/slices/loginSlice';
import {
    passwordRecoveryReducer as recruiterAppPasswordRecoveryReducer,
    PasswordRecoveryState as RecruiterAppPasswordRecoveryState,
} from '../../recruiter/units/authorization/redux/slices/passwordRecoverySlice';
import {
    settingsReducer as recruiterAppSettingsReducer,
    SettingsState as RecruiterAppSettingsState,
} from '../../recruiter/units/settings/redux/slice/settingsSlice';
import {
    profileReducer as recruiterAppProfileReducer,
    ProfileState as RecruiterAppProfileState,
} from '../../recruiter/units/settings/redux/slice/profileSlice';

import { recruiterAppSaga } from '../../recruiter/redux/sagas/recruiterAppSaga';
import { authSaga as recruiterAppAuthSaga } from '../../recruiter/units/authorization/redux/sagas/authSaga';
import { settingsSaga as recruiterAppSettingsSaga } from '../../recruiter/units/settings/redux/sagas/settingsSaga';

// Employer
import { employerAppReducer, EmployerAppState } from '../../employer/redux/slices/employerAppSlice';
import { employerSaga } from '../../employer/redux/sagas/employerAppSaga';

import { vacancyFormReducer, VacancyFormState } from '../../employer/units/vacancy/redux/slices/vacancyFormSlice';
import { vacancyListReducer, VacancyListState } from '../../employer/units/vacancy/redux/slices/vacancyListSlice';
import { vacancyFormSaga } from '../../employer/units/vacancy/redux/sagas/vacancyFormSaga';
import { vacancyListSaga } from '../../employer/units/vacancy/redux/sagas/vacancyListSaga';

interface RootReducer {
    app: Reducer<AppState>;

    workerApp?: Reducer<WorkerAppState>;
    workerAppSettings?: Reducer<WorkerAppSettingsState>;
    workerAppSignUp?: Reducer<WorkerAppSignUpState>;
    workerAppQuickSignUp?: Reducer<WorkerAppQuickSignUpState>;
    workerAppLogin?: Reducer<WorkerAppLoginState>;
    workerAppPasswordRecovery?: Reducer<WorkerAppPasswordRecoveryState>;
    workerAppQuickApply?: Reducer<WorkerAppQuickApplyState>;

    recruiterApp?: Reducer<RecruiterAppState>;
    recruiterAppSettings?: Reducer<RecruiterAppSettingsState>;
    recruiterAppSignUp?: Reducer<RecruiterAppSignUpState>;
    recruiterAppLogin?: Reducer<RecruiterAppLoginState>;
    recruiterAppPasswordRecovery?: Reducer<RecruiterAppPasswordRecoveryState>;
    recruiterAppProfile?: Reducer<RecruiterAppProfileState>;

    employerApp?: Reducer<EmployerAppState>;
    vacancyForm?: Reducer<VacancyFormState>;
    vacancy?: Reducer<VacancyState>;
    vacancyList?: Reducer<VacancyListState>;
    employerResponses?: Reducer<ResponsesState>;
}

interface RootStore {
    rootReducer: RootReducer;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rootSaga?: Saga<any[]>;
}

export const storeComposer = (): RootStore => {
    if (!window.GLOBALS) {
        console.error('App cannot be initialized. GLOBALS config not found!');
    }

    if (!window.GLOBALS.appType) {
        console.error('Value "appType" is not present in GLOBALS config!');
    }

    const rootReducer = {
        app: appReducer,
    };

    switch (window.GLOBALS.appType) {
        case AppType.Empoloyer:
            return {
                rootReducer: {
                    ...rootReducer,
                    employerApp: employerAppReducer,
                    vacancy: vacancyReducer,
                    vacancyForm: vacancyFormReducer,
                    vacancyList: vacancyListReducer,
                    employerResponses: responsesReducer,
                },
                rootSaga: function* rootSaga() {
                    yield fork(employerSaga);
                    yield fork(vacancySaga);
                    yield fork(vacancyFormSaga);
                    yield fork(vacancyListSaga);
                    yield fork(responsesSaga);
                },
            };
        case AppType.Worker:
            return {
                rootReducer: {
                    ...rootReducer,
                    workerApp: workerAppReducer,
                    workerAppSettings: workerAppSettingsReducer,
                    workerAppSignUp: workerAppSignUpReducer,
                    workerAppQuickSignUp: workerAppQuickSignUpReducer,
                    workerAppLogin: workerAppLoginReducer,
                    workerAppPasswordRecovery: workerAppPasswordRecoveryReducer,
                    workerAppQuickApply: workerAppQuickApplyReducer,
                },
                rootSaga: function* rootSaga() {
                    yield fork(workerAppSaga);
                    yield fork(workerAppAuthSaga);
                    yield fork(workerAppSettingsSaga);
                    yield fork(workerAppQuickApplySaga);
                },
            };

        case AppType.Recruiter:
            return {
                rootReducer: {
                    ...rootReducer,
                    recruiterApp: recruiterAppReducer,
                    recruiterAppSettings: recruiterAppSettingsReducer,
                    recruiterAppSignUp: recruiterAppSignUpReducer,
                    recruiterAppLogin: recruiterAppLoginReducer,
                    recruiterAppPasswordRecovery: recruiterAppPasswordRecoveryReducer,
                    recruiterAppProfile: recruiterAppProfileReducer,
                },
                rootSaga: function* rootSaga() {
                    yield fork(recruiterAppSaga);
                    yield fork(recruiterAppAuthSaga);
                    yield fork(recruiterAppSettingsSaga);
                },
            };
    }
};
